import { useEffect, useRef } from 'react'

/**
 * Attach a brief `.shimmer-on-update` class to a ref'd element whenever `dep`
 * changes identity — triggers the 300ms `interference-shimmer` keyframe defined
 * in src/index.css.
 *
 * Skips the initial mount so cards don't flash on page load. Forces a reflow
 * between class removal and re-addition so repeated updates re-trigger the
 * CSS animation cleanly (setting the same class back doesn't restart it).
 */
export function useShimmerOnUpdate<T>(dep: T) {
  const ref = useRef<HTMLDivElement>(null)
  const mounted = useRef(false)

  useEffect(() => {
    if (!mounted.current) { mounted.current = true; return }
    const el = ref.current
    if (!el || dep == null) return

    el.classList.remove('shimmer-on-update')
    void el.offsetWidth // force reflow → animation restart on re-add
    el.classList.add('shimmer-on-update')

    const t = window.setTimeout(() => el.classList.remove('shimmer-on-update'), 320)
    return () => window.clearTimeout(t)
  }, [dep])

  return ref
}
